<script setup lang="ts">
import { computed, ref } from 'vue'
import { useBreakpoints } from '@/lib/utils'
import BaseIcon from '@/components/ui/icon/BaseIcon.vue'

const { isLarge } = useBreakpoints()

const props = defineProps<{
	images: string[]
	videos?: string[]
}>()

const currentIndex = ref(0)
const setIndex = (index: number) => {
	currentIndex.value = index
}

interface ThumbnailItem {
	type: 'image' | 'video'
	url: string
}

const combinedThumbnailItems = computed<ThumbnailItem[]>(() => {
	const imageItems: ThumbnailItem[] = props.images.map(url => ({
		type: 'image',
		url,
	}))
	const videoItems: ThumbnailItem[] =
		props.videos?.map(url => ({ type: 'video', url })) ?? []
	const thumbnailItems = [...imageItems, ...videoItems]

	return thumbnailItems
})

const additionalThumbnailsCount = computed(() => {
	return combinedThumbnailItems.value.length - 4
})
</script>

<template>
	<div
		class="relative h-[240px] w-full flex-shrink-0 mdp:aspect-square mdp:h-min mdp:w-[calc((100%-32px)*1/3)] mdl:w-[calc((100%-40px)*1/3)] lg:flex lg:h-[300px] lg:gap-16px"
	>
		<div v-if="isLarge" class="flex w-[50px] flex-col gap-8px">
			<template
				v-for="(item, index) in combinedThumbnailItems.slice(0, 5)"
				:key="index"
			>
				<div
					class="aspect-square cursor-pointer overflow-hidden rounded-md border border-n-100 bg-white hover:border-n-950"
					:class="{ 'border-2 !border-o-500': currentIndex === index }"
				>
					<div
						v-if="additionalThumbnailsCount > 1 && index === 4"
						class="typography-h5-str grid h-full w-full place-content-center"
					>
						+{{ additionalThumbnailsCount }}
					</div>

					<template v-else>
						<img
							v-if="item.type === 'image'"
							:src="item.url"
							alt="Product Image"
							class="h-full w-full object-contain"
							@click="setIndex(index)"
						/>

						<!-- Todo: Handle video display here -->
						<div v-else></div>
					</template>
				</div>
			</template>
		</div>

		<img
			:src="images[currentIndex]"
			alt="Product Image"
			class="mx-auto aspect-square w-[240px] object-contain mdp:w-full mdp:rounded-lg lg:ml-0 lg:w-[300px]"
		/>

		<div
			v-if="!isLarge"
			class="typography-c1-reg absolute bottom-12px right-12px flex h-24px items-center gap-4px rounded-sm bg-wg-50 px-8px"
		>
			<BaseIcon name="multiple-photo" class="h-16px w-16px text-n-950" />
			<div>1/{{ images.length }}</div>
		</div>
	</div>
</template>
